import { useState, useRef, useEffect } from 'react';
import { getTagBackgroundColor, formatText } from './projectsData';

export const TextColumn = ({ column, project, selectedTags }) => {
    const { width, content } = column;
    

    return (
        <div className={`${width} p-4 lg:p-6`}>
            {content.showTitle && (
                <h2 className="text-2xl font-semibold text-black mb-3">
                    {project.title}
                </h2>
            )}
            
            {content.showDescription && (
                <p className="text-black text-base mb-4 leading-relaxed">
                    {formatText(project.description)}
                </p>
            )}
            
            {content.showTags && (
                <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                        <span
                            key={tag}
                            className={`px-2 py-1 rounded-md text-gray-800/70 text-xs font-regular
                                ${getTagBackgroundColor(tag, selectedTags?.includes(tag))}`}
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// Хук: возвращает true, когда элемент попал (или почти попал) во вьюпорт.
// Once === true — значит, дальше можно не следить (грузим один раз и отключаемся).
const useInView = () => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        // Если браузер совсем древний и IntersectionObserver недоступен —
        // просто считаем, что элемент виден, чтобы ничего не сломать.
        if (!('IntersectionObserver' in window)) {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, {
            rootMargin: '300px', // начинаем грузить чуть заранее, до появления на экране
            threshold: 0.01,
        });

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return [ref, inView];
};

// Один медиа-блок: до попадания во вьюпорт показывает лёгкий постер/плейсхолдер,
// после — грузит видео (webm/mp4) или, если видео не задано, обычную картинку (gif/png/jpg).
const LazyMedia = ({ image }) => {
    const [ref, inView] = useInView();
    const [loaded, setLoaded] = useState(false);

    const hasVideo = Boolean(image.srcWebm || image.srcMp4);
    // placeholder — маленькая, сильно сжатая версия того же кадра (можно те же 660х360,
    // но веса в разы меньше за счёт агрессивного сжатия/меньшего размера файла)
    const placeholder = image.poster || image.placeholder;

    return (
        <div ref={ref} className="relative w-full h-full">
            {placeholder && (
                <img
                    src={placeholder}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-0' : 'opacity-100'}`}
                />
            )}

            {inView && hasVideo && (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={placeholder}
                    onLoadedData={() => setLoaded(true)}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                >
                    {image.srcWebm && <source src={image.srcWebm} type="video/webm" />}
                    {image.srcMp4 && <source src={image.srcMp4} type="video/mp4" />}
                </video>
            )}

            {inView && !hasVideo && (
                <img
                    src={image.src}
                    alt={image.alt}
                    onLoad={() => setLoaded(true)}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                />
            )}
        </div>
    );
};

export const ImageColumn = ({ column }) => {
    const { width, images, position } = column;
    const marginClass = position === 'left' ? 'lg:mr-12' : 'lg:ml-8';

    return (
        <div className={`${width} px-4 lg:px-6 py-4 ${marginClass}`}>
            {images.map((image, index) => (
                <div key={index} className="mb-0 aspect-[660/360] overflow-hidden rounded-lg">
                    {/* Точные пропорции 660×360 */}
                    <LazyMedia image={image} />
                </div>
            ))}
        </div>
    );
};

export const PortfolioColumnRenderer = ({ column, project, selectedTags }) => {
    switch (column.type) {
        case 'text':
            return <TextColumn column={column} project={project} selectedTags={selectedTags} />;
        
        case 'image':
            return <ImageColumn column={column} />;
        
        default:
            return null;
    }
};